import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Card,
  CardHeader,
  Caption1,
  Label,
  makeStyles,
  mergeClasses,
  Spinner,
  Text,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import { ArrowDownLeftRegular, ArrowUpRightRegular } from '@fluentui/react-icons';
import type { EChartsOption } from 'echarts';
import { useAuth } from '../auth/TeamsAuthProvider';
import { NativeDateInput } from '../common/NativeDateInput';
import { TimezoneSelector } from '../common/TimezoneSelector';
import { useTimezone } from '../common/TimezoneProvider';
import { PageHeader } from '../common/PageHeader';
import { ViewAsAction } from '../common/ViewAsAction';
import { KpiTile, type KpiTileProps } from '../common/KpiTile';
import { Chart } from '../common/charts/Chart';
import { useChartColors } from '../common/charts/chartTheme';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { StatusPill } from '../common/StatusPill';
import { api } from '../../services/api';
import type {
  MyRecentCall,
  MyRecentCallsResponse,
  MySummary,
  MyTopContactsResponse,
  MyTrends,
} from '../../types';
import { durationCoverageLabel, reportingTimeLabel } from '../../utils/callDisplay';
import { deriveOutcome, outcomeTone } from '../../utils/interactionOutcome';
import { interactionPathFor } from '../../utils/routes';
import { strings } from '../../strings/en';

const useStyles = makeStyles({
  content: {
    padding: tokens.spacingHorizontalL,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '720px',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'end',
    width: '100%',
    marginBottom: tokens.spacingVerticalXL,
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  statHint: {
    color: tokens.colorNeutralForeground3,
  },
  dataQualityLine: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    marginTop: `calc(-1 * ${tokens.spacingVerticalM})`,
    marginBottom: tokens.spacingVerticalL,
  },
  fullWidthPanel: {
    marginBottom: tokens.spacingVerticalXL,
  },
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  activityChartContainer: {
    height: '280px',
  },
  inlineStats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  inlineStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  listMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  recentCallPrimaryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  recentCallDirectionIcon: {
    flexShrink: 0,
  },
  recentCallRow: {
    display: 'grid',
    // time · name/context (flexible) · duration (narrow) · badges
    gridTemplateColumns: 'minmax(132px, auto) 1fr 72px auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  recentCallTime: {
    whiteSpace: 'nowrap',
  },
  recentCallDuration: {
    textAlign: 'right',
  },
  clickable: {
    cursor: 'pointer',
  },
  recentBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  statusBanner: {
    marginBottom: tokens.spacingVerticalL,
  },
  statusBannerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  statusBannerText: {
    color: tokens.colorNeutralForeground3,
  },
  statusBannerError: {
    marginBottom: tokens.spacingVerticalL,
    borderLeft: `4px solid ${tokens.colorPaletteRedForeground1}`,
  },
});

// User-visible copy lives together here (not scattered inline) so a future
// French translation pass can work from whole sentences with placeholders
// instead of hunting down concatenated fragments.
const STRINGS = {
  subtitleBase:
    'A personal view of your call activity, recent conversations, and routing patterns.',
  scopedTo: (customer: string) => ` Scoped to ${customer}.`,
  greetingWithLocation: (name: string, location: string) => `Signed in as ${name} · ${location}`,
  greeting: (name: string) => `Signed in as ${name}`,
};

// Exported so a regression test can assert the collapse rule (spec §5: the
// three named data-quality counts leave the KPI row for a single caption
// line, shown only when at least one is non-zero) without rendering the
// whole data-fetching page.
export function buildDataQualityLine(counts: {
  unknown_inbound_outcome_calls: number;
  completion_fallback_count: number;
  inbound_unknown_calls: number;
}): string | null {
  const items = [
    { label: 'Unknown inbound outcomes', value: counts.unknown_inbound_outcome_calls },
    { label: 'Start time unavailable', value: counts.completion_fallback_count },
    { label: 'Unknown Route', value: counts.inbound_unknown_calls },
  ].filter((item) => item.value > 0);
  return items.length > 0 ? items.map((item) => `${item.label}: ${item.value}`).join(' · ') : null;
}

/** Minimum call count before the Activity Trend chart earns its place (spec §5). */
export const TREND_MIN_CALLS = 10;

export function shouldShowTrend(totalCalls: number): boolean {
  return totalCalls >= TREND_MIN_CALLS;
}

/**
 * Builds the four-tile KPI hero row for `/my` (spec §5): Conversations,
 * Missed, People, Talk time, in that order -- never more, never fewer.
 * `peopleCount` comes from the server-side `distinct_contact_count`, not a
 * truncated top-contacts list, so the People tile is never an undercount.
 */
export function buildMyCallsTiles(summary: MySummary | null, peopleCount: number): KpiTileProps[] {
  const t = strings.myCalls.tiles;
  const conversations = (summary?.total_calls ?? 0) - (summary?.missed_inbound_calls ?? 0);
  const missedHint =
    summary?.routing_data_available && (summary?.routed_calls ?? 0) > 0
      ? t.missedHintQueueCaveat
      : t.missedHint;
  return [
    {
      size: 'tile',
      label: t.conversations,
      value: conversations,
      caption: t.conversationsHint,
      subItems: [
        { label: t.inbound, value: summary?.inbound_calls ?? 0 },
        { label: t.outbound, value: summary?.outbound_calls ?? 0 },
        ...(summary?.routing_data_available && (summary?.routed_calls ?? 0) > 0
          ? [{ label: t.viaQueue, value: summary?.routed_calls ?? 0 }]
          : []),
      ],
    },
    {
      size: 'tile',
      label: t.missed,
      value: summary?.missed_inbound_calls ?? 0,
      caption: missedHint,
      valueTone: (summary?.missed_inbound_calls ?? 0) > 0 ? 'negative' : 'neutral',
    },
    {
      size: 'tile',
      label: t.people,
      value: peopleCount,
      caption: t.peopleHint,
    },
    {
      size: 'tile',
      label: t.talkTime,
      value: formatDuration(summary?.total_talk_time_seconds ?? null),
      caption: summary
        ? durationCoverageLabel(summary.duration_known_count, summary.duration_total_count, summary.duration_is_partial) ?? undefined
        : undefined,
      subItems: [
        {
          label: t.average,
          value: formatDuration(summary?.average_talk_time_seconds == null ? null : Math.round(summary.average_talk_time_seconds)),
        },
      ],
    },
  ];
}

type DatePreset = 'today' | 'last7' | 'last30' | 'custom';

function getTimeZoneCalendarDate(timeZone: string, baseDate = new Date()): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(baseDate);

  const year = Number(parts.find((part) => part.type === 'year')?.value ?? '1970');
  const month = Number(parts.find((part) => part.type === 'month')?.value ?? '1');
  const day = Number(parts.find((part) => part.type === 'day')?.value ?? '1');

  return new Date(Date.UTC(year, month - 1, day));
}

function formatDateParam(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function buildPresetRange(preset: Exclude<DatePreset, 'custom'>, timeZone: string) {
  const endDate = getTimeZoneCalendarDate(timeZone);
  const startDate = new Date(endDate);

  if (preset === 'last7') {
    startDate.setUTCDate(startDate.getUTCDate() - 6);
  } else if (preset === 'last30') {
    startDate.setUTCDate(startDate.getUTCDate() - 29);
  }

  return {
    startDate: formatDateParam(startDate),
    endDate: formatDateParam(endDate),
  };
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatPhoneNumber(phone: string | null): string {
  if (!phone) {
    return '-';
  }

  const digitsOnly = phone.replace(/\D/g, '');
  const tenDigit =
    digitsOnly.length === 11 && digitsOnly.startsWith('1')
      ? digitsOnly.slice(1)
      : digitsOnly;

  if (tenDigit.length === 10) {
    return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
  }

  return phone;
}

function normalizeLabel(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function isPhoneLikeLabel(value: string): boolean {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
}

function labelsMatch(left: string | null, right: string | null): boolean {
  if (!left || !right) {
    return false;
  }

  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function formatRecentCallParty(
  name: string | null,
  number: string | null,
  customerName: string | null,
): string {
  const normalizedName = normalizeLabel(name);
  const normalizedCustomer = normalizeLabel(customerName);
  const formattedNumber = formatPhoneNumber(number);

  if (
    !normalizedName ||
    labelsMatch(normalizedName, normalizedCustomer) ||
    isPhoneLikeLabel(normalizedName)
  ) {
    return formattedNumber;
  }

  return `${normalizedName} (${formattedNumber})`;
}

/**
 * Which direction cue (icon + accessible label) a Recent interactions row
 * shows, exported so a plain-function test can assert the mapping without
 * rendering the row (spec §2: purple = clickable/brand, teal = informative
 * highlight -- inbound is the brand hue, outbound is the chart-layer teal).
 */
export function getRecentCallDirectionCue(direction: string): {
  direction: 'inbound' | 'outbound';
  ariaLabel: string;
} {
  const isInbound = direction === 'inbound';
  return {
    direction: isInbound ? 'inbound' : 'outbound',
    ariaLabel: isInbound ? 'Inbound' : 'Outbound',
  };
}

function getRecentCallPrimaryLabel(call: MyRecentCall): string {
  if (call.direction === 'inbound') {
    return formatRecentCallParty(call.from_name, call.from_number, call.customer_name);
  }

  return formatPhoneNumber(call.to_number);
}

function getRecentCallContext(call: MyRecentCall): string | null {
  const branchName = normalizeLabel(call.branch_name);
  const customerName = normalizeLabel(call.customer_name);

  if (branchName && !labelsMatch(branchName, customerName)) {
    return `Branch: ${branchName}`;
  }

  if (call.direction === 'inbound' && call.to_number) {
    return `To ${formatPhoneNumber(call.to_number)}`;
  }

  if (call.direction === 'outbound' && call.from_number) {
    return `From ${formatPhoneNumber(call.from_number)}`;
  }

  return null;
}

function parseIsoDateOnly(value: string): Date {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1));
}

type ActivityChartPoint = {
  key: string;
  label: string;
  inboundCalls: number;
  outboundCalls: number;
  totalCalls: number;
};

type ActivityGroupingMode = 'day' | 'week' | 'month';

function startOfWeek(date: Date): Date {
  const copy = new Date(date);
  const day = copy.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setUTCDate(copy.getUTCDate() + diff);
  copy.setUTCHours(0, 0, 0, 0);
  return copy;
}

function formatTrendLabel(date: Date, mode: 'day' | 'week' | 'month'): string {
  if (mode === 'month') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  if (mode === 'week') {
    const end = new Date(date);
    end.setUTCDate(end.getUTCDate() + 6);
    return `${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })} - ${end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })}`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function getActivityGroupingMode(
  selectedStartDate?: string,
  selectedEndDate?: string,
  pointCount = 0,
): ActivityGroupingMode {
  let daySpan = pointCount;
  if (selectedStartDate && selectedEndDate) {
    const start = parseIsoDateOnly(selectedStartDate);
    const end = parseIsoDateOnly(selectedEndDate);
    daySpan = Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1,
    );
  }

  if (daySpan <= 31) {
    return 'day';
  }

  return daySpan <= 120 ? 'week' : 'month';
}

function buildActivityChartPoints(
  points: MyTrends['daily_points'] | undefined,
  selectedStartDate?: string,
  selectedEndDate?: string,
): { points: ActivityChartPoint[]; mode: ActivityGroupingMode } {
  if (!points?.length) {
    return { points: [], mode: 'day' };
  }

  const mode = getActivityGroupingMode(selectedStartDate, selectedEndDate, points.length);
  if (mode === 'day') {
    return {
      mode,
      points: points.map((point) => {
        const date = new Date(point.date);
        return {
          key: point.date,
          label: formatTrendLabel(date, 'day'),
          inboundCalls: point.inbound_calls,
          outboundCalls: point.outbound_calls,
          totalCalls: point.total_calls,
        };
      }),
    };
  }

  const buckets = new Map<string, ActivityChartPoint>();
  for (const point of points) {
    const date = new Date(point.date);
    const bucketDate =
      mode === 'week'
        ? startOfWeek(date)
        : new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    const key = bucketDate.toISOString();
    const existing = buckets.get(key);

    if (existing) {
      existing.inboundCalls += point.inbound_calls;
      existing.outboundCalls += point.outbound_calls;
      existing.totalCalls += point.total_calls;
      continue;
    }

    buckets.set(key, {
      key,
      label: formatTrendLabel(bucketDate, mode),
      inboundCalls: point.inbound_calls,
      outboundCalls: point.outbound_calls,
      totalCalls: point.total_calls,
    });
  }

  return {
    mode,
    points: Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key)),
  };
}

interface MyDashboardProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

export function MyDashboard({ emulationPanelOpen, onToggleEmulationPanel }: MyDashboardProps) {
  const styles = useStyles();
  const { user, isAuthenticated } = useAuth();
  const { timezone, formatDate, formatDateTime } = useTimezone();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultRange = buildPresetRange('last7', timezone);
  const startDate = searchParams.get('start_date') || defaultRange.startDate;
  const endDate = searchParams.get('end_date') || defaultRange.endDate;
  const datePreset = (searchParams.get('date_preset') as DatePreset | null) || 'last7';
  const [summary, setSummary] = useState<MySummary | null>(null);
  const [trends, setTrends] = useState<MyTrends | null>(null);
  const [topContacts, setTopContacts] = useState<MyTopContactsResponse | null>(null);
  const [recentCalls, setRecentCalls] = useState<MyRecentCallsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const effectiveUserKey = useMemo(
    () => [
      user?.actual_email ?? '',
      user?.actual_role ?? '',
      user?.email ?? '',
      user?.role ?? '',
      user?.customer_account_id ?? '',
      user?.emulated_customer_account_id ?? '',
      user?.emulated_user_email ?? '',
      user?.is_emulating ? '1' : '0',
    ].join('|'),
    [
      user?.actual_email,
      user?.actual_role,
      user?.customer_account_id,
      user?.email,
      user?.emulated_customer_account_id,
      user?.emulated_user_email,
      user?.is_emulating,
      user?.role,
    ],
  );

  const updateFilters = useCallback((updates: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.has('start_date') || searchParams.has('end_date')) {
      return;
    }

    updateFilters({
      date_preset: 'last7',
      start_date: defaultRange.startDate,
      end_date: defaultRange.endDate,
    });
  }, [defaultRange.endDate, defaultRange.startDate, searchParams, updateFilters]);

  useEffect(() => {
    async function fetchMyDashboard() {
      if (!isAuthenticated) {
        return;
      }

      const requestId = ++requestIdRef.current;

      try {
        setIsLoading(true);
        setError(null);
        const dashboardData = await api.getMyDashboard(startDate, endDate, timezone, 6, 8);

        if (requestId !== requestIdRef.current) {
          return;
        }

        setSummary(dashboardData.summary);
        setTrends(dashboardData.trends);
        setTopContacts(dashboardData.top_contacts);
        setRecentCalls(dashboardData.recent_calls);
        setError(null);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load My Calls dashboard');
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }

    void fetchMyDashboard();
  }, [effectiveUserKey, endDate, isAuthenticated, startDate, timezone]);

  const periodLabel = useMemo(() => {
    if (!summary?.period_start || !summary?.period_end) {
      return '';
    }
    return `${formatDate(summary.period_start)} - ${formatDate(summary.period_end)}`;
  }, [formatDate, summary?.period_end, summary?.period_start]);

  const dataQualityLine = useMemo(
    () =>
      buildDataQualityLine({
        unknown_inbound_outcome_calls: summary?.unknown_inbound_outcome_calls ?? 0,
        completion_fallback_count: summary?.completion_fallback_count ?? 0,
        inbound_unknown_calls: summary?.inbound_unknown_calls ?? 0,
      }),
    [summary?.unknown_inbound_outcome_calls, summary?.completion_fallback_count, summary?.inbound_unknown_calls],
  );

  const activityChart = useMemo(
    () => buildActivityChartPoints(trends?.daily_points, startDate, endDate),
    [endDate, startDate, trends?.daily_points],
  );
  const activityColors = useChartColors();
  const activityOption: EChartsOption = useMemo(
    () => ({
      grid: { left: 48, right: 16, top: 36, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const items = params as Array<{
            axisValue: string;
            seriesName: string;
            value: number | null;
          }>;
          if (!items?.length) return '';
          const label = items[0].axisValue;
          const get = (name: string) => Number(items.find((p) => p.seriesName === name)?.value ?? 0);
          const inboundValue = get('Inbound');
          const outboundValue = get('Outbound');
          const totalValue = inboundValue + outboundValue;
          return [
            `<div style="font-weight:600;margin-bottom:6px">${label}</div>`,
            `<div>Inbound: ${inboundValue}</div>`,
            `<div>Outbound: ${outboundValue}</div>`,
            `<div>Total: ${totalValue}</div>`,
          ].join('');
        },
      },
      // Shared theme sets `legend.show: false` by default; this is a 2-series
      // stacked bar (same shape as branch `DailyVolumeChart`) so it needs the
      // same explicit built-in legend override rather than external markup.
      legend: {
        show: true,
        data: ['Inbound', 'Outbound'],
        top: 4,
        right: 8,
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: activityColors.neutral },
      },
      xAxis: {
        type: 'category',
        data: activityChart.points.map((point) => point.label),
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
      },
      series: [
        {
          name: 'Inbound',
          type: 'bar',
          stack: 'calls',
          data: activityChart.points.map((point) => point.inboundCalls),
          itemStyle: { color: activityColors.inbound, borderRadius: [0, 0, 0, 0] },
          barMaxWidth: 44,
        },
        {
          name: 'Outbound',
          type: 'bar',
          stack: 'calls',
          data: activityChart.points.map((point) => point.outboundCalls),
          itemStyle: { color: activityColors.outbound, borderRadius: [6, 6, 0, 0] },
          barMaxWidth: 44,
        },
      ],
    }),
    [activityChart.points, activityColors],
  );
  const isTrendAggregated = activityChart.mode !== 'day';
  const activityTrendDescription =
    activityChart.mode === 'month'
      ? 'Inbound and outbound activity grouped by month for longer ranges'
      : activityChart.mode === 'week'
        ? 'Inbound and outbound activity grouped by week for readability'
        : 'Inbound and outbound activity across the selected period';
  const hasLoadedData = Boolean(summary || trends || topContacts || recentCalls);
  const showInitialLoading = isLoading && !hasLoadedData;
  const showRefreshing = isLoading && hasLoadedData;

  if (!isAuthenticated) {
    return <LoadingState variant="page" label="Loading your calls…" />;
  }

  const headerSubtitle = user?.name
    ? user.branch_names[0]
      ? STRINGS.greetingWithLocation(user.name, user.branch_names[0])
      : STRINGS.greeting(user.name)
    : undefined;

  // PageHeader (and the nav it carries) must render as soon as the user is
  // known, even while the page's own data is still loading -- otherwise
  // navigation disappears during the initial fetch (caught by
  // smoke.spec.ts's "my page renders the core controls" @live test).
  const header = (
    <PageHeader
      title="My Calls"
      subtitle={headerSubtitle}
      breadcrumb="My Calls"
      actions={
        <ViewAsAction
          emulationPanelOpen={emulationPanelOpen}
          onToggleEmulationPanel={onToggleEmulationPanel}
        />
      }
    />
  );

  if (showInitialLoading) {
    return (
      <div>
        {header}
        <LoadingState variant="page" label="Loading your calls…" />
      </div>
    );
  }

  return (
    <div>
      {header}

      <div className={styles.content}>
      <div className={styles.intro}>
        <Text className={styles.subtitle}>
          {STRINGS.subtitleBase}
          {(user?.is_emulating || user?.actual_role === 'platform_admin') && user?.customer_filter
            ? STRINGS.scopedTo(user.customer_filter)
            : ''}
        </Text>
        {periodLabel && <Text className={styles.subtitle}>{periodLabel}</Text>}
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <Label size="small">Quick Range</Label>
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' }}>
            <Button
              appearance={datePreset === 'today' ? 'primary' : 'secondary'}
              onClick={() => {
                const range = buildPresetRange('today', timezone);
                updateFilters({ date_preset: 'today', start_date: range.startDate, end_date: range.endDate });
              }}
            >
              Today
            </Button>
            <Button
              appearance={datePreset === 'last7' ? 'primary' : 'secondary'}
              onClick={() => {
                const range = buildPresetRange('last7', timezone);
                updateFilters({ date_preset: 'last7', start_date: range.startDate, end_date: range.endDate });
              }}
            >
              Last 7 Days
            </Button>
            <Button
              appearance={datePreset === 'last30' ? 'primary' : 'secondary'}
              onClick={() => {
                const range = buildPresetRange('last30', timezone);
                updateFilters({ date_preset: 'last30', start_date: range.startDate, end_date: range.endDate });
              }}
            >
              Last 30 Days
            </Button>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <Label size="small">Start Date</Label>
          <NativeDateInput
            value={startDate}
            max={endDate || undefined}
            onChange={(value) => updateFilters({
              date_preset: 'custom',
              start_date: value || undefined,
            })}
          />
        </div>

        <div className={styles.filterGroup}>
          <Label size="small">End Date</Label>
          <NativeDateInput
            value={endDate}
            min={startDate || undefined}
            onChange={(value) => updateFilters({
              date_preset: 'custom',
              end_date: value || undefined,
            })}
          />
        </div>

        <div className={styles.filterGroup}>
          <TimezoneSelector />
        </div>
      </div>

      {showRefreshing && (
        <Card appearance="filled" className={styles.statusBanner}>
          <CardHeader
            header={
              <div className={styles.statusBannerContent}>
                <Spinner size="tiny" label="Refreshing My Calls..." />
                <Text weight="semibold">Refreshing current view</Text>
                <Text className={styles.statusBannerText}>
                  Keeping the current data visible while we fetch the latest totals.
                </Text>
              </div>
            }
          />
        </Card>
      )}

      {error && (
        <ErrorState
          className={styles.statusBannerError}
          title="Could not load My Calls"
          message={error}
        />
      )}

      {!error && (summary?.total_calls ?? 0) === 0 && (
        (user?.owned_number_count ?? 0) === 0 ? (
          // My Calls is scoped entirely to the user's own phone numbers, so
          // with none linked there is nothing to show in any date range.
          // Saying "no calls in this range" here would send people hunting
          // through dates for data that cannot appear.
          <EmptyState
            title="No phone number linked"
            hint={
              user?.is_emulating
                ? 'This user has no phone number linked to their account yet, so there is no call activity to show. Choose a user with a linked number to preview their experience.'
                : 'Your account does not have a phone number linked yet, so we cannot show your call activity. This is a setup step, not an error. Ask your administrator to link your number — your calls will appear here automatically once it is.'
            }
          />
        ) : (
          <EmptyState
            title="No calls found"
            hint={
              user?.is_emulating
                ? 'This emulated user has no matching calls in the selected range. Try a different date range, or choose a user with recent activity.'
                : 'No matching calls were found in the selected range. Try a different date range, or if you are using View As, choose a user with recent activity.'
            }
          />
        )
      )}

      <div className={styles.cardsGrid}>
        {buildMyCallsTiles(summary, summary?.distinct_contact_count ?? 0).map((p) => (
          <KpiTile key={p.label} {...p} />
        ))}
      </div>

      {/* "Haven't spoken to lately" removed for slice 1 (owner send-back F4):
          a sales-relationship concept that does not fit a school's My Calls.
          See the PR body for the modular-card design question. */}
      <Card appearance="filled" className={styles.fullWidthPanel}>
        <CardHeader
          header={<Text weight="semibold">{strings.myCalls.peopleCard}</Text>}
          description={strings.myCalls.peopleCardHint}
        />
        <div className={styles.panelBody}>
          {topContacts?.contacts.length ? topContacts.contacts.map((contact) => (
            <div
              key={`${contact.phone_number}-${contact.last_call_at}`}
              className={mergeClasses(styles.listItem, styles.clickable)}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/calls?search=${encodeURIComponent(contact.phone_number ?? '')}&start_date=${startDate}&end_date=${endDate}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/calls?search=${encodeURIComponent(contact.phone_number ?? '')}&start_date=${startDate}&end_date=${endDate}`);
                }
              }}
            >
              <div className={styles.listMeta}>
                <Text weight="semibold">{contact.display_name || formatPhoneNumber(contact.phone_number)}</Text>
                <Text size={200}>{formatPhoneNumber(contact.phone_number)}</Text>
                <Text size={200}>{strings.myCalls.inOut(contact.inbound_calls, contact.outbound_calls)}</Text>
              </div>
              <div className={styles.listMeta} style={{ alignItems: 'flex-end' }}>
                <Badge appearance="outline">{strings.myCalls.calls(contact.call_count)}</Badge>
                <Text size={200}>{strings.myCalls.lastSpoke(formatDateTime(contact.last_call_at))}</Text>
              </div>
            </div>
          )) : (
            <EmptyState compact title={strings.myCalls.peopleEmpty} />
          )}
        </div>
      </Card>

      <Card appearance="filled" className={styles.fullWidthPanel}>
        <CardHeader
          header={<Text weight="semibold">{strings.myCalls.recentCard}</Text>}
          description={strings.myCalls.recentCardHint}
          action={
            <Button
              appearance="secondary"
              onClick={() => navigate(`/calls?start_date=${startDate}&end_date=${endDate}&date_preset=${datePreset}`)}
            >
              {strings.myCalls.viewFullHistory}
            </Button>
          }
        />
        <div className={styles.panelBody}>
          {recentCalls?.calls.length ? recentCalls.calls.map((call) => {
            const outcomeWord = deriveOutcome({ direction: call.direction, outcome: call.outcome });
            const tone = outcomeTone(outcomeWord);
            return (
              <div
                key={call.session_id}
                className={mergeClasses(styles.recentCallRow, styles.clickable)}
                role="button"
                tabIndex={0}
                onClick={() => navigate(interactionPathFor(call.session_id))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(interactionPathFor(call.session_id));
                  }
                }}
              >
                <div className={styles.recentCallTime}>
                  <Text className={styles.recentCallTime} block>{formatDateTime(call.reporting_time, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>
                  <Text size={200} block>{reportingTimeLabel(call.reporting_time_basis)}</Text>
                </div>
                <div className={styles.listMeta}>
                  {(() => {
                    const recentCallContext = getRecentCallContext(call);
                    const directionCue = getRecentCallDirectionCue(call.direction);
                    const DirectionIcon = directionCue.direction === 'inbound' ? ArrowDownLeftRegular : ArrowUpRightRegular;
                    const directionColor = directionCue.direction === 'inbound'
                      ? tokens.colorBrandForeground1
                      : activityColors.outbound;

                    return (
                      <>
                        <Text weight="semibold" className={styles.recentCallPrimaryLabel}>
                          <Tooltip content={directionCue.ariaLabel} relationship="label">
                            <DirectionIcon
                              className={styles.recentCallDirectionIcon}
                              style={{ color: directionColor, fontSize: 16 }}
                              aria-label={directionCue.ariaLabel}
                            />
                          </Tooltip>
                          {getRecentCallPrimaryLabel(call)}
                        </Text>
                        {recentCallContext && <Text size={200}>{recentCallContext}</Text>}
                      </>
                    );
                  })()}
                </div>
                <Text className={styles.recentCallDuration}>{formatDuration(call.duration_seconds)}</Text>
                <div className={styles.recentBadges}>
                  <StatusPill tone={tone === 'negative' ? 'negative' : tone === 'warning' ? 'warning' : 'neutral'}>
                    {outcomeWord}
                  </StatusPill>
                  {call.direction === 'inbound' && call.was_queue_routed && <StatusPill tone="info">Routed</StatusPill>}
                  {call.was_transferred && <StatusPill tone="neutral" outline>Transferred</StatusPill>}
                </div>
              </div>
            );
          }) : (
            <EmptyState compact title={strings.myCalls.recentEmpty} />
          )}
        </div>
        <Text size={200} className={styles.statHint}>{strings.myCalls.durationFootnote}</Text>
      </Card>

      {shouldShowTrend(summary?.total_calls ?? 0) && (
        <Card appearance="filled" className={styles.fullWidthPanel}>
          <CardHeader
            header={<Text weight="semibold">{strings.myCalls.trendCard}</Text>}
            description={activityTrendDescription}
          />
          <div className={styles.panelBody}>
            <div className={styles.activityChartContainer}>
              <Chart
                option={activityOption}
                height="100%"
                ariaLabel="Inbound and outbound call activity trend, stacked bar chart"
              />
            </div>
            {isTrendAggregated && (
              <Text size={200} className={styles.statHint}>
                {activityChart.mode === 'week'
                  ? 'Weekly buckets are used automatically once the selected range exceeds 31 days.'
                  : 'Monthly buckets are used automatically once the selected range exceeds 120 days.'}
              </Text>
            )}
          </div>
        </Card>
      )}

      <Accordion collapsible className={styles.fullWidthPanel}>
        <AccordionItem value="how-counted">
          <AccordionHeader>{strings.myCalls.howCounted}</AccordionHeader>
          <AccordionPanel>
            <div className={styles.inlineStats}>
              <div className={styles.inlineStat}>
                <Text size={200}>{strings.myCalls.directCalls}</Text>
                <Text weight="semibold">{summary?.inbound_direct_calls ?? 0}</Text>
              </div>
              <div className={styles.inlineStat}>
                <Text size={200}>{strings.myCalls.routedCalls}</Text>
                <Text weight="semibold">{summary?.routed_calls ?? 0}</Text>
              </div>
              {summary && !summary.routing_data_available && (
                <div className={styles.inlineStat}>
                  <Text size={200}>{strings.myCalls.routingUnavailable}</Text>
                </div>
              )}
              {summary?.ambiguous_routed_calls ? (
                <div className={styles.inlineStat}>
                  <Text size={200}>{strings.myCalls.routedOverlap(summary.ambiguous_routed_calls)}</Text>
                </div>
              ) : null}
            </div>
            {dataQualityLine && <Caption1 className={styles.dataQualityLine}>{dataQualityLine}</Caption1>}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      </div>
    </div>
  );
}
